import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { PeriodontogramaService } from "app/services/periodontograma.service";
import { PeriodontogramaConfigService } from "app/services/periodontograma-config.service";
import { EstadoPeriodontograma, EntradaDientePerio } from "app/models/periodontograma.interface";
import { entradaVacia, entradaEsVacia } from "app/shared/periodontograma/catalogo-periodontograma";
import { PerioPopoverComponent } from "app/shared/periodontograma/perio-popover/perio-popover.component";

@Component({
  selector: "app-crear-periodontograma",
  templateUrl: "./crear-periodontograma.component.html",
  styleUrls: ["./crear-periodontograma.component.scss"],
})
export class CrearPeriodontogramaComponent implements OnInit {
  @Input() pacienteId!: number;
  @Input() medicoId!: number;
  @Input() centroMedicoId!: number;
  @Input() fecha?: string | Date;
  @Input() observaciones?: string | null;
  @Input() fechaNacimiento?: string;
  @Input() mostrarGuardar = true;
  @Output() periodontogramaCreado = new EventEmitter<number>();

  estadoPerio: EstadoPeriodontograma = {};
  denticion: "adulto" | "nino" | "mixta" = "adulto";
  ultimoFecha?: string;
  guardando = false;

  constructor(
    private service: PeriodontogramaService,
    private configService: PeriodontogramaConfigService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.configService.cargar().subscribe();
    this.service.ultimoPorPaciente(this.pacienteId).subscribe({
      next: (o) => (this.ultimoFecha = o.fecha),
      error: () => (this.ultimoFecha = undefined),
    });
    this.denticion = this.denticionSugerida();
  }

  private denticionSugerida(): "adulto" | "nino" | "mixta" {
    if (!this.fechaNacimiento) return "adulto";
    const nac = new Date(this.fechaNacimiento);
    if (isNaN(nac.getTime())) return "adulto";
    const hoy = new Date();
    let edad = hoy.getFullYear() - nac.getFullYear();
    const m = hoy.getMonth() - nac.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
    if (edad < 6) return "nino";
    if (edad <= 12) return "mixta";
    return "adulto";
  }

  empezarNuevo(): void {
    this.estadoPerio = {};
  }

  precargarUltimo(): void {
    this.service.ultimoPorPaciente(this.pacienteId).subscribe({
      next: (o) => {
        this.estadoPerio = JSON.parse(JSON.stringify(o.periodontograma_estado || {}));
        if (o.denticion) this.denticion = o.denticion;
      },
      error: () =>
        this.snackBar.open("Sin periodontogramas previos para este paciente", "OK", { duration: 3000 }),
    });
  }

  onDientePerioClick(e: { fdi: string }): void {
    this.abrirPerioPopover(e.fdi);
  }

  abrirPerioPopover(fdi: string): void {
    const entryActual: EntradaDientePerio = this.estadoPerio[fdi]
      ? JSON.parse(JSON.stringify(this.estadoPerio[fdi]))
      : entradaVacia();
    const ref = this.dialog.open(PerioPopoverComponent, {
      data: { fdi, entry: entryActual },
      width: "560px",
      panelClass: "cara-popover-panel",
      autoFocus: false,
    });
    ref.afterClosed().subscribe((entryNueva: EntradaDientePerio | undefined) => {
      if (entryNueva === undefined) return;
      const nuevo = { ...this.estadoPerio };
      if (entryNueva.presente === false || !entradaEsVacia(entryNueva)) {
        nuevo[fdi] = entryNueva;
      } else {
        delete nuevo[fdi];
      }
      this.estadoPerio = nuevo;
    });
  }

  guardar(): void {
    if (!this.pacienteId || !this.medicoId || !this.centroMedicoId) {
      this.snackBar.open("Completá médico y centro médico", "OK", { duration: 3000 });
      return;
    }
    this.guardando = true;
    this.service
      .crear({
        idPaciente: this.pacienteId,
        medico: this.medicoId,
        centroMedico: this.centroMedicoId,
        fecha: this.fechaParaGuardar(),
        observaciones: this.observaciones ?? null,
        denticion: this.denticion,
        periodontograma_estado: this.estadoPerio,
      })
      .subscribe({
        next: (r) => {
          this.guardando = false;
          this.periodontogramaCreado.emit(r.id);
        },
        error: () => {
          this.guardando = false;
          this.snackBar.open("Error al guardar el periodontograma", "OK", { duration: 4000 });
        },
      });
  }

  private fechaParaGuardar(): string {
    const d = this.fecha ? new Date(this.fecha) : new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
}
